import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, downloadFile } from '../lib/api'
import { useEngagements } from '../lib/queries'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { money, number, thaiDateTime } from '../lib/format'

// AML / STR pack (part 158). The scan is advisory: every indicator has to be turned into an
// STR draft by a person, and a draft only becomes filed after an explicit confirmation.

type Thresholds = {
  cash_threshold_satang: number
  structuring_window_hours: number
  structuring_count: number
}

type Indicator = {
  code: string
  subject_type: 'transaction' | 'counterparty'
  subject_id: string
  subject_name?: string
  detail: string
  amount_at_risk_satang: number
  tx_count: number
  evidence_ids: string[]
}

type ScanResult = {
  generated_at: string
  engagement_id: string
  thresholds: Thresholds
  total: number
  counts: Record<string, number>
  indicators: Indicator[]
  elapsed_ms: number
}

type STRStatus = 'draft' | 'ready' | 'filed'

type STRDraft = {
  id: string
  subject_type: 'transaction' | 'counterparty'
  subject_id: string
  reason_code: string
  narrative: string
  total_satang: number
  tx_count: number
  status: STRStatus
  created_at: string
  updated_at: string
}

const CODES = ['CASH_OVER_THRESHOLD', 'STRUCTURING', 'ROUND_AMOUNT_REPEAT', 'OFF_SHORE_COUNTERPARTY'] as const

const reasonLabel: Record<string, string> = {
  CASH_OVER_THRESHOLD: 'รายการเดี่ยวเกินเกณฑ์',
  STRUCTURING: 'แบ่งรายการใต้เกณฑ์',
  ROUND_AMOUNT_REPEAT: 'ยอดกลมทำซ้ำ',
  OFF_SHORE_COUNTERPARTY: 'คู่สัญญาต่างประเทศ',
}

const reasonHint: Record<string, string> = {
  CASH_OVER_THRESHOLD: 'รายการเดียวที่มีมูลค่าถึงเกณฑ์รายงาน',
  STRUCTURING: 'หลายรายการใต้เกณฑ์ในเวลากระชั้นชิด',
  ROUND_AMOUNT_REPEAT: 'ยอดกลมเดิมซ้ำกับคู่สัญญารายเดิมภายใน 7 วัน',
  OFF_SHORE_COUNTERPARTY: 'เอกสารอ้างอิงมีรหัสประเทศนอกเขตอำนาจ',
}

const subjectLabel: Record<string, string> = { transaction: 'รายการ', counterparty: 'คู่สัญญา' }

const statusLabel: Record<STRStatus, string> = { draft: 'ร่าง', ready: 'พร้อมยื่น', filed: 'ยื่นแล้ว' }

export function AML() {
  const qc = useQueryClient()
  const engagements = useEngagements()
  const rows = engagements.data ?? []

  const [engagementId, setEngagementId] = useState('')
  const activeId = engagementId || rows[0]?.id || ''

  const thresholds = useQuery({
    queryKey: ['aml-thresholds'],
    queryFn: () => api<Thresholds>('/v1/aml/thresholds'),
  })
  const drafts = useQuery({
    queryKey: ['aml-str'],
    queryFn: () => api<STRDraft[]>('/v1/aml/str'),
  })

  const [cash, setCash] = useState('')
  const [windowHours, setWindowHours] = useState('')
  const [count, setCount] = useState('')
  const [savedAt, setSavedAt] = useState('')

  useEffect(() => {
    if (!thresholds.data) return
    setCash((thresholds.data.cash_threshold_satang / 100).toFixed(2))
    setWindowHours(String(thresholds.data.structuring_window_hours))
    setCount(String(thresholds.data.structuring_count))
  }, [thresholds.data])

  const saveThresholds = useMutation({
    mutationFn: () => apiWrite<Thresholds>('/v1/aml/thresholds', 'PUT', {
      cash_threshold_satang: Math.round(Number(cash) * 100),
      structuring_window_hours: Number(windowHours),
      structuring_count: Number(count),
    }),
    onSuccess: (data) => {
      qc.setQueryData(['aml-thresholds'], data)
      setSavedAt(new Date().toISOString())
    },
  })

  const [scan, setScan] = useState<ScanResult | null>(null)
  const runScan = useMutation({
    mutationFn: (id: string) => apiWrite<ScanResult>('/v1/aml/scan', 'POST', { engagement_id: id }),
    onSuccess: (data) => setScan(data),
  })

  const createSTR = useMutation({
    mutationFn: (ind: Indicator) => apiWrite<STRDraft>('/v1/aml/str', 'POST', {
      subject_type: ind.subject_type,
      subject_id: ind.subject_id,
      reason_code: ind.code,
      narrative: `${reasonLabel[ind.code] ?? ind.code} — ${ind.detail}`,
      total_satang: ind.amount_at_risk_satang,
      tx_count: ind.tx_count,
    }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['aml-str'] }),
  })

  const move = useMutation({
    mutationFn: (v: { id: string; status: STRStatus }) =>
      apiWrite<STRDraft>(`/v1/aml/str/${v.id}`, 'PATCH', { status: v.status }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['aml-str'] }),
  })

  const cashSatang = Math.round(Number(cash) * 100)
  const thresholdValid = cashSatang >= 100_000 && cashSatang <= 100_000_000_000 &&
    Number(windowHours) >= 1 && Number(windowHours) <= 720 &&
    Number(count) >= 2 && Number(count) <= 50

  const draftRows = drafts.data ?? []
  const indicators = scan?.indicators ?? []
  const counts = scan?.counts ?? {}

  return (
    <>
      <PageHead
        title="AML และรายงานธุรกรรมต้องสงสัย"
        sub="สแกนบัญชีแยกประเภทด้วยเกณฑ์รายงาน แล้วยกเป็น STR ทีละเรื่อง — ทุกการยื่นต้องมีคนยืนยัน"
        actions={
          <button className="btn btn-ghost" onClick={() => void downloadFile('/v1/aml/str.csv', 'auditai-str.csv')}>
            ดาวน์โหลด STR (CSV)
          </button>
        }
      />

      <div className="card card-pad">
        <div className="card-label">เกณฑ์รายงานขององค์กร</div>
        <p className="dim" style={{ fontSize: 12.5, marginTop: 10, lineHeight: 1.7 }}>
          เกณฑ์เงินสดเริ่มต้น 2,000,000 บาท ตามประกาศ AML ของไทย ปรับได้เพื่อให้ตรงกับนโยบายลูกค้าแต่ละราย
          การสแกนใช้เกณฑ์นี้กับรายการที่ลงบัญชีแล้ว (posted) ทุกช่องทาง เพราะบัญชีแยกประเภทยังไม่มีการระบุว่าเป็นเงินสด
        </p>

        {thresholds.isLoading ? <Skel h={90} /> : (
          <div className="grid-3" style={{ marginTop: 16 }}>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="aml-cash">เกณฑ์เงินสด (บาท)</label>
              <input id="aml-cash" className="input" type="number" min={1000} step="0.01"
                value={cash} onChange={(e) => setCash(e.target.value)} />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="aml-window">หน้าต่างการแบ่งรายการ (ชั่วโมง)</label>
              <input id="aml-window" className="input" type="number" min={1} max={720}
                value={windowHours} onChange={(e) => setWindowHours(e.target.value)} />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="aml-count">จำนวนรายการขั้นต่ำ</label>
              <input id="aml-count" className="input" type="number" min={2} max={50}
                value={count} onChange={(e) => setCount(e.target.value)} />
            </div>
          </div>
        )}

        <div className="row" style={{ marginTop: 16 }}>
          <button className="btn btn-primary" disabled={!thresholdValid || saveThresholds.isPending}
            onClick={() => saveThresholds.mutate()}>
            {saveThresholds.isPending ? 'กำลังบันทึก…' : 'บันทึกเกณฑ์'}
          </button>
          <span className="dim" style={{ fontSize: 11.5 }}>
            {savedAt ? `บันทึกเมื่อ ${thaiDateTime(savedAt)}` : 'มีผลกับการสแกนครั้งถัดไป'}
          </span>
        </div>
        {saveThresholds.isError ? (
          <div className="form-error">{saveThresholds.error instanceof Error ? saveThresholds.error.message : 'บันทึกเกณฑ์ไม่สำเร็จ'}</div>
        ) : null}
      </div>

      <div className="card card-pad" style={{ marginTop: 18 }}>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <div className="card-label">สแกนโครงการ</div>
          <div className="row" style={{ marginLeft: 'auto', gap: 8 }}>
            <select className="select" aria-label="เลือกโครงการตรวจสอบ" value={activeId}
              onChange={(e) => { setEngagementId(e.target.value); setScan(null) }} style={{ minWidth: 280 }}>
              {rows.length === 0 && <option value="">ยังไม่มีโครงการ</option>}
              {rows.map((e) => <option key={e.id} value={e.id}>{e.code} · {e.title}</option>)}
            </select>
            <button className="btn btn-primary" disabled={!activeId || runScan.isPending}
              onClick={() => runScan.mutate(activeId)}>
              {runScan.isPending ? 'กำลังสแกน…' : 'สแกน AML'}
            </button>
          </div>
        </div>

        {runScan.isError ? (
          <div className="form-error">{runScan.error instanceof Error ? runScan.error.message : 'สแกนไม่สำเร็จ'}</div>
        ) : null}

        {!scan ? (
          <div style={{ marginTop: 16 }}>
            <Empty title="ยังไม่ได้สแกนโครงการนี้" body="เลือกโครงการแล้วกดสแกน ระบบจะตรวจ 4 รูปแบบความเสี่ยงบนรายการที่ลงบัญชีแล้ว" />
          </div>
        ) : (
          <>
            <div className="grid-4" style={{ marginTop: 16 }}>
              <Stat label="รายการที่เข้าเงื่อนไข" value={scan.total} unit="เรื่อง"
                foot={`ใช้เวลา ${scan.elapsed_ms} ms`} />
              <Stat label="มูลค่ารวม" value={money(indicators.reduce((s, i) => s + i.amount_at_risk_satang, 0), { compact: true })}
                foot={`สแกนเมื่อ ${thaiDateTime(scan.generated_at)}`} />
              <Stat label="รายการเดี่ยวเกินเกณฑ์" value={number(counts.CASH_OVER_THRESHOLD ?? 0)} unit="รายการ"
                foot={`เกณฑ์ ${money(scan.thresholds.cash_threshold_satang)}`} />
              <Stat label="รูปแบบที่พบ" value={CODES.filter((c) => (counts[c] ?? 0) > 0).length} unit="จาก 4"
                foot="แสดงรายละเอียดด้านล่าง" />
            </div>

            <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
              {CODES.map((code) => (
                <span key={code} className={`chip${(counts[code] ?? 0) > 0 ? ' gold' : ''}`}
                  title={reasonHint[code]}>
                  {reasonLabel[code]} · {counts[code] ?? 0}
                </span>
              ))}
            </div>

            {indicators.length === 0 ? (
              <div style={{ marginTop: 16 }}>
                <Empty title="ไม่พบรูปแบบต้องสงสัย" body="ไม่มีรายการเข้าเงื่อนไขกับเกณฑ์ปัจจุบัน ลองทบทวนเกณฑ์หรือช่วงเวลาของโครงการ" />
              </div>
            ) : (
              <div className="table-wrap" style={{ marginTop: 16 }}>
                <table className="data">
                  <thead>
                    <tr>
                      <th>รูปแบบ</th><th>ผู้ถูกกล่าวหา</th><th>รายละเอียด</th>
                      <th className="num">รายการ</th><th className="num">มูลค่า (บาท)</th>
                      <th className="num">หลักฐาน</th><th />
                    </tr>
                  </thead>
                  <tbody>
                    {indicators.map((ind) => (
                      <tr key={`${ind.code}-${ind.subject_type}-${ind.subject_id}`} style={{ cursor: 'default' }}>
                        <td>
                          <div>{reasonLabel[ind.code] ?? ind.code}</div>
                          <div className="dim mono" style={{ fontSize: 10.5 }}>{ind.code}</div>
                        </td>
                        <td>
                          <div>{ind.subject_name || ind.subject_id}</div>
                          <div className="dim mono" style={{ fontSize: 10.5 }}>
                            {subjectLabel[ind.subject_type]} · {ind.subject_id}
                          </div>
                        </td>
                        <td style={{ maxWidth: 360 }}>{ind.detail}</td>
                        <td className="num">{number(ind.tx_count)}</td>
                        <td className="num">{money(ind.amount_at_risk_satang)}</td>
                        <td className="num">{number(ind.evidence_ids.length)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <button className="btn btn-sm btn-ghost"
                            disabled={createSTR.isPending && createSTR.variables?.subject_id === ind.subject_id}
                            onClick={() => createSTR.mutate(ind)}>
                            {createSTR.isSuccess && createSTR.variables?.subject_id === ind.subject_id ? 'บันทึกแล้ว' : 'สร้าง STR'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {createSTR.isError ? (
              <div className="form-error">{createSTR.error instanceof Error ? createSTR.error.message : 'สร้าง STR ไม่สำเร็จ'}</div>
            ) : null}
          </>
        )}
      </div>

      <div className="card-label" style={{ marginTop: 26, marginBottom: 10 }}>รายงานธุรกรรมต้องสงสัย (STR)</div>

      {drafts.isLoading ? <Skel h={180} /> : draftRows.length === 0 ? (
        <div className="card">
          <Empty title="ยังไม่มี STR" body="สร้างเรื่องจากตัวบ่งชี้ด้านบน ระบบจะรวมเรื่องเดิมไว้เป็นฉบับเดียวตามคู่สัญญาและเหตุผล" />
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>ผู้ถูกกล่าวหา</th><th>เหตุผล</th><th>คำบรรยาย</th>
                <th className="num">มูลค่า (บาท)</th><th className="num">รายการ</th>
                <th>สถานะ</th><th>สร้างเมื่อ</th><th />
              </tr>
            </thead>
            <tbody>
              {draftRows.map((d) => (
                <tr key={d.id} style={{ cursor: 'default' }}>
                  <td>
                    <div className="mono" style={{ fontSize: 11.5 }}>{d.subject_id}</div>
                    <div className="dim" style={{ fontSize: 10.5 }}>{subjectLabel[d.subject_type]}</div>
                  </td>
                  <td>{reasonLabel[d.reason_code] ?? d.reason_code}</td>
                  <td style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={d.narrative}>{d.narrative}</td>
                  <td className="num">{money(d.total_satang)}</td>
                  <td className="num">{number(d.tx_count)}</td>
                  <td><span className={`chip${d.status === 'filed' ? ' gold' : ''}`}>{statusLabel[d.status]}</span></td>
                  <td className="dim" style={{ fontSize: 11.5 }}>{thaiDateTime(d.created_at)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {d.status === 'draft' && (
                      <button className="btn btn-sm btn-ghost" disabled={move.isPending}
                        onClick={() => move.mutate({ id: d.id, status: 'ready' })}>ส่งตรวจ</button>
                    )}
                    {d.status === 'ready' && (
                      <>
                        <button className="btn btn-sm btn-ghost" disabled={move.isPending}
                          onClick={() => move.mutate({ id: d.id, status: 'draft' })}>ส่งกลับแก้ไข</button>
                        {' '}
                        <button className="btn btn-sm btn-primary" disabled={move.isPending}
                          onClick={() => move.mutate({ id: d.id, status: 'filed' })}>ยืนยันยื่นแล้ว</button>
                      </>
                    )}
                    {d.status === 'filed' && <span className="dim" style={{ fontSize: 11 }}>ปิดเรื่องแล้ว</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {move.isError ? (
        <div className="form-error">{move.error instanceof Error ? move.error.message : 'เปลี่ยนสถานะไม่สำเร็จ'}</div>
      ) : null}

      <div className="dim" style={{ marginTop: 14, fontSize: 11.5 }}>
        ตัวเลขเงินแสดงเป็นบาทจากหน่วยสตางค์ (หาร 100) · ไฟล์ CSV เป็น UTF-8 มี BOM เปิดใน Excel ได้ทันที
      </div>
    </>
  )
}
